import type { InspectionItem, ReportLanguage } from '../../types';
import type { KatalogFelder } from '../../services/mangelKatalog/spalten';
import type { Vorbelegung } from '../../services/mangelKatalog/vorbelegung';
import { hatPlatzhalter } from '../../services/mangelKatalog/vorlage';

/**
 * Schritt „Standardmangel“ im Handy-/Tablet-Assistenten
 * (MobileInspectionWizard, docs/mangelkatalog-plan.md „Ablauf für den
 * Nutzer → Handy / Tablet“): reine Regeln — wann der Schritt kommt, wohin
 * Zurück führt und welche Felder die Auswahl in den Entwurf bzw. in den neuen
 * Eintrag schreibt.
 */

/** Eintragstypen des Assistenten (gleich `ItemType` im Wizard). */
export type AssistentTyp = 'defect' | 'info' | 'good' | 'clearance' | 'general';

/**
 * Katalog in allen Berichtssprachen (seit 28.09.; bei 'en' englisch, bei 'de'
 * und 'de/en' deutsch — katalogSprache), aber ohne Vorbefüllung: die
 * n.i.O.-Übergabe aus der Checkliste bringt ihren Text schon mit und
 * überspringt den Schritt.
 */
export function katalogImAssistenten(language: ReportLanguage | undefined, initialData: Partial<InspectionItem> | null | undefined): boolean {
    const sprache = language ?? 'de';
    return (sprache === 'de' || sprache === 'en' || sprache === 'de/en') && !initialData;
}

/**
 * Schritt nach der Typwahl: Mangel → „Standardmangel“ (wenn aktiv);
 * Freimeldung mit offenen Mängeln → Auswahl des Mangels; sonst „Details“.
 */
export function schrittNachTypwahl(
    typ: AssistentTyp,
    p: { katalogAktiv: boolean; freimeldungMitAuswahl: boolean },
): 'katalog' | 'pick' | 'what' {
    if (typ === 'clearance' && p.freimeldungMitAuswahl) return 'pick';
    if (typ === 'defect' && p.katalogAktiv) return 'katalog';
    return 'what';
}

/**
 * Zurück aus „Details“: gezielte Freimeldung → Auswahl des Mangels; Mangel
 * mit aktivem Katalog → „Standardmangel“ (auch nach „Freitext“); sonst Typ.
 */
export function schrittVorDetails(p: {
    katalogAktiv: boolean;
    itemType: AssistentTyp;
    gezielteFreimeldung: boolean;
}): 'katalog' | 'pick' | 'type' {
    if (p.gezielteFreimeldung) return 'pick';
    if (p.katalogAktiv && p.itemType === 'defect') return 'katalog';
    return 'type';
}

/**
 * Verknüpfung zum Katalog lösen ([×] am Chip, anderer Typ, „Freitext“). Der
 * Eintrag ist neu, es gibt nichts „bewusst zu löschen“ — deshalb undefined,
 * nicht null. Titel, Beschreibung und Vorschrift bleiben stehen.
 */
export function katalogFelderLoesen(): { katalogKey: undefined; katalogKategorie: undefined } {
    return { katalogKey: undefined, katalogKategorie: undefined };
}

/**
 * Vorbelegung → Felder für den Entwurf. Frist und Sofortmaßnahme kommen nur
 * mit, wenn die Vorbelegung sie liefert (Dringlichkeit im Katalog); ohne
 * Angabe bleibt die Frist des Entwurfs (+14 Tage). Eine selbst eingetragene
 * Frist bleibt — dieselbe Regel wie beim Wechsel des Typs.
 */
export function felderAusVorbelegung(v: Vorbelegung, fristSelbstGesetzt: boolean): Partial<InspectionItem> {
    const { deadline, immediateAction, ...rest } = v.felder;
    const felder: Partial<InspectionItem> = { ...rest };
    if (!fristSelbstGesetzt) {
        if (deadline !== undefined) felder.deadline = deadline;
        if (immediateAction !== undefined) felder.immediateAction = immediateAction;
    }
    return felder;
}

/** Gelber Hinweis „Angabe ergänzen“: live aus Titel + Beschreibung, nicht nur beim Wählen. */
export function angabeFehlt(d: Pick<Partial<InspectionItem>, 'title' | 'observation'>): boolean {
    return hatPlatzhalter(`${d.title ?? ''} ${d.observation ?? ''}`);
}

/**
 * Katalog-Felder für den neuen Eintrag (handleSave): nur beim Mangel und nur
 * gesetzte Werte — wie `verortung` per bedingtem Spread. Ein neuer Eintrag
 * hat nichts „bewusst gelöscht“, null fällt deshalb ebenso weg wie undefined.
 */
export function katalogFelderFuerEintrag(itemType: AssistentTyp, d: KatalogFelder): KatalogFelder {
    if (itemType !== 'defect') return {};
    return {
        ...(typeof d.katalogKey === 'string' && d.katalogKey ? { katalogKey: d.katalogKey } : {}),
        ...(typeof d.katalogKategorie === 'string' && d.katalogKategorie ? { katalogKategorie: d.katalogKategorie } : {}),
    };
}

/**
 * Frist und Sofortmaßnahme bei der Typwahl — immer GEMEINSAM (Codex-Review
 * 27.09.): Vorher setzte „Zurück → Typ → Mangel“ nach einem „sofort“-Standard-
 * mangel die Frist wieder auf +14 Tage und ließ die Sofortmaßnahme stehen.
 * - Derselbe Typ erneut gewählt: beides bleibt, wie es ist.
 * - Selbst eingetragene Frist (deadlineTouched): bleibt; die Sofortmaßnahme nur beim Mangel.
 * - Sonst: Mangel → Standardfrist ohne Sofortmaßnahme, andere Typen → weder noch.
 */
export function fristNachTypwahl(p: {
    typ: string;
    bisherTyp: string;
    deadlineTouched: boolean;
    bisher: { deadline?: string; immediateAction?: boolean };
    standardFrist: string;
}): { deadline: string; immediateAction: boolean } {
    const bisherFrist = p.bisher.deadline ?? '';
    const bisherSofort = !!p.bisher.immediateAction;
    if (p.typ === p.bisherTyp) return { deadline: bisherFrist, immediateAction: bisherSofort };
    if (p.deadlineTouched) return { deadline: bisherFrist, immediateAction: p.typ === 'defect' && bisherSofort };
    return p.typ === 'defect'
        ? { deadline: p.standardFrist, immediateAction: false }
        : { deadline: '', immediateAction: false };
}
